import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Fallback values for development/testing
const fallbackUrl = 'https://bqqwqwakpltqytszbllf.supabase.co';
const fallbackKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImJxcXdxd2FrcGx0cXl0c3pibGxmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTExMjIyMzksImV4cCI6MjA2NjY5ODIzOX0.5POrbJ548xO-5Qav7TfnuXkL1xsSuldKkFZ0LianrOo';

// Log the Supabase URL and key being used (for debugging)
console.log('Supabase URL being used:', supabaseUrl || fallbackUrl);
console.log('Supabase key is set:', (supabaseAnonKey || fallbackKey) ? 'Yes' : 'No');

// Create the Supabase client with proper options
export const supabase = createClient(
  supabaseUrl || fallbackUrl, 
  supabaseAnonKey || fallbackKey,
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true
    }
  }
);

export type Agent = {
  id: string;
  name: string;
  description: string;
  source: string;
  source_url: string;
  repository_url?: string;
  author: string;
  category?: string;
  tags: string[];
  language?: string;
  framework?: string;
  stars: number;
  downloads: number;
  last_updated?: string;
  is_verified: boolean;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
  price?: number;
  pricing_model?: 'free' | 'one-time' | 'subscription_eligible';
  creator_user_id?: string;
};

export type AgentCategory = {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  created_at: string;
};

export type ScrapingLog = {
  id: string;
  source: string;
  status: 'success' | 'error' | 'in_progress';
  agents_found: number;
  error_message?: string;
  execution_time?: number;
  created_at: string;
};

export type AgentAnalysis = {
  id: string;
  agent_id: string;
  usefulness_score: number;
  complexity_level: 'beginner' | 'intermediate' | 'advanced';
  capabilities: string[];
  use_cases: string[];
  step_by_step_guide: any[];
  requirements: string[];
  installation_steps: string[];
  pros: string[];
  cons: string[];
  estimated_setup_time: string;
  last_analyzed: string;
  created_at: string;
  updated_at: string;
};

export type AgentInteraction = {
  id: string;
  agent_id: string;
  user_id?: string;
  interaction_type: 'view' | 'analyze' | 'generate_ui' | 'use_ui';
  metadata: Record<string, any>;
  created_at: string;
};

export type AgentCodeAnalysis = {
  id: string;
  agent_id: string;
  is_code_based: boolean;
  code_type?: string;
  programming_language?: string;
  main_functionality?: string;
  input_parameters: any[];
  output_format?: string;
  ui_feasible: boolean;
  ui_complexity?: 'simple' | 'moderate' | 'complex';
  analysis_summary: string;
  code_snippets: any[];
  dependencies: string[];
  api_endpoints: any[];
  last_analyzed: string;
  created_at: string;
  updated_at: string;
};

export type AgentUIGeneration = {
  id: string;
  agent_id: string;
  code_analysis_id: string;
  ui_type: 'form' | 'dashboard' | 'tool' | 'api_client' | 'interactive';
  ui_components: any[];
  ui_code: string;
  css_styles?: string;
  javascript_code?: string;
  api_integration: Record<string, any>;
  status: 'generating' | 'generated' | 'deployed' | 'error';
  usage_count: number;
  last_used?: string;
  created_at: string;
  updated_at: string;
};

export type AssistantConversation = {
  id: string;
  user_id?: string;
  session_id: string;
  message_type: 'user' | 'assistant' | 'system';
  content: string;
  metadata: Record<string, any>;
  created_at: string;
};

export type UserPreferences = {
  id: string;
  user_id: string;
  preferences: Record<string, any>;
  favorite_agents: string[];
  search_history: string[];
  interaction_patterns: Record<string, any>;
  created_at: string;
  updated_at: string;
};

export type AgentPurchase = {
  id: string;
  user_id: string;
  agent_id: string;
  payment_intent_id: string;
  amount: number;
  currency: string;
  pricing_model: 'one-time' | 'subscription' | 'usage-based';
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  stripe_customer_id?: string;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
};

export type AgentSubscription = {
  id: string;
  user_id: string;
  agent_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  plan_amount: number;
  plan_currency: string;
  plan_interval: 'month' | 'year';
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
};

export type SubscriptionTier = {
  id: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  agent_limit: number;
  features: string[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type UserSubscription = {
  id: string;
  user_id: string;
  subscription_tier_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  metadata: Record<string, any>;
  created_at: string;
  updated_at: string;
};